import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Briefcase, MapPin, DollarSign, ExternalLink, Search, Building,
  Linkedin, Wrench, Rocket, GraduationCap, Clock, TrendingUp,
  Users, Globe, Star, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Job Categories
const JOB_CATEGORIES = [
  { id: 'all', label: 'All Jobs', icon: Briefcase },
  { id: 'entry', label: 'Entry Level', icon: GraduationCap },
  { id: 'trades', label: 'Skilled Trades', icon: Wrench },
  { id: 'tech', label: 'Technology', icon: Globe },
  { id: 'healthcare', label: 'Healthcare', icon: Users },
  { id: 'business', label: 'Business', icon: Building }
];

// Sample job listings (would be fetched from LinkedIn/Indeed API in production)
const JOB_LISTINGS = [
  {
    id: '1',
    title: 'Junior Software Developer',
    company: 'TechStart Inc.',
    location: 'Remote / San Francisco, CA',
    salary: '$60,000 - $80,000',
    type: 'Full-time',
    category: 'tech',
    level: 'entry',
    posted: '2 days ago',
    apply_url: 'https://linkedin.com/jobs',
    description: 'Looking for recent graduates with knowledge of Python, JavaScript. Training provided.',
    requirements: ['Bachelor\'s degree in CS or related', 'Basic programming skills', 'Willingness to learn']
  },
  {
    id: '2',
    title: 'Apprentice Electrician',
    company: 'PowerLine Electric',
    location: 'Austin, TX',
    salary: '$18 - $25/hour',
    type: 'Full-time',
    category: 'trades',
    level: 'entry',
    posted: '1 day ago',
    apply_url: 'https://indeed.com/jobs',
    description: 'Join our apprenticeship program. No experience required. Earn while you learn.',
    requirements: ['High school diploma or GED', 'Valid driver\'s license', 'Ability to lift 50 lbs']
  },
  {
    id: '3',
    title: 'Medical Assistant Trainee',
    company: 'HealthFirst Clinic',
    location: 'Chicago, IL',
    salary: '$35,000 - $42,000',
    type: 'Full-time',
    category: 'healthcare',
    level: 'entry',
    posted: '3 days ago',
    apply_url: 'https://linkedin.com/jobs',
    description: 'Entry-level position with certification training provided. Great benefits.',
    requirements: ['High school diploma', 'Strong communication skills', 'Compassionate attitude']
  },
  {
    id: '4',
    title: 'HVAC Technician Apprentice',
    company: 'Cool Air Solutions',
    location: 'Phoenix, AZ',
    salary: '$40,000 - $55,000',
    type: 'Full-time',
    category: 'trades',
    level: 'entry',
    posted: '5 days ago',
    apply_url: 'https://indeed.com/jobs',
    description: 'Paid apprenticeship program. EPA certification provided. Great growth potential.',
    requirements: ['Mechanical aptitude', 'Clean driving record', 'Physical fitness']
  },
  {
    id: '5',
    title: 'Marketing Coordinator',
    company: 'Brand Builders Agency',
    location: 'New York, NY (Hybrid)',
    salary: '$45,000 - $55,000',
    type: 'Full-time',
    category: 'business',
    level: 'entry',
    posted: '1 week ago',
    apply_url: 'https://linkedin.com/jobs',
    description: 'Entry-level marketing role. Perfect for recent graduates interested in digital marketing.',
    requirements: ['Bachelor\'s degree', 'Social media savvy', 'Creative mindset']
  },
  {
    id: '6',
    title: 'Welder Helper',
    company: 'Steel Works Manufacturing',
    location: 'Houston, TX',
    salary: '$16 - $22/hour',
    type: 'Full-time',
    category: 'trades',
    level: 'entry',
    posted: '4 days ago',
    apply_url: 'https://indeed.com/jobs',
    description: 'No experience needed. Will train the right candidate. Path to certified welder.',
    requirements: ['High school diploma', 'Steel-toe boots', 'Reliable transportation']
  },
  {
    id: '7',
    title: 'IT Help Desk Technician',
    company: 'DataCore Systems',
    location: 'Denver, CO',
    salary: '$40,000 - $50,000',
    type: 'Full-time',
    category: 'tech',
    level: 'entry',
    posted: '2 days ago',
    apply_url: 'https://linkedin.com/jobs',
    description: 'Entry-level IT support. CompTIA certification training provided.',
    requirements: ['Associate degree or certifications', 'Customer service skills', 'Basic troubleshooting']
  },
  {
    id: '8',
    title: 'Dental Assistant Trainee',
    company: 'Smile Dental Group',
    location: 'Los Angeles, CA',
    salary: '$18 - $24/hour',
    type: 'Full-time',
    category: 'healthcare',
    level: 'entry',
    posted: '6 days ago',
    apply_url: 'https://indeed.com/jobs',
    description: 'On-the-job training provided. Work alongside experienced dental professionals.',
    requirements: ['High school diploma', 'Good hand-eye coordination', 'Friendly personality']
  }
];

// Entrepreneurship Resources
const ENTREPRENEURSHIP_RESOURCES = [
  {
    title: 'SCORE Mentorship',
    description: 'Free business mentoring and education from experienced entrepreneurs',
    url: 'https://www.score.org/',
    icon: Users
  },
  {
    title: 'SBA Learning Center',
    description: 'Free online courses on starting and managing a business',
    url: 'https://www.sba.gov/learning-center',
    icon: GraduationCap
  },
  {
    title: 'Young Entrepreneurs Council',
    description: 'Network with young business owners and access resources',
    url: 'https://yec.co/',
    icon: Rocket
  },
  {
    title: 'Small Business Grants',
    description: 'Find grants and funding for your business idea',
    url: 'https://www.grants.gov/',
    icon: DollarSign
  }
];

// LinkedIn Job Search URLs by category
const LINKEDIN_SEARCH_URLS = {
  entry: 'https://www.linkedin.com/jobs/search/?f_E=1%2C2&keywords=entry%20level',
  trades: 'https://www.linkedin.com/jobs/search/?keywords=apprentice%20electrician%20plumber%20hvac',
  tech: 'https://www.linkedin.com/jobs/search/?f_E=2&keywords=junior%20developer%20software',
  healthcare: 'https://www.linkedin.com/jobs/search/?keywords=medical%20assistant%20healthcare%20entry',
  business: 'https://www.linkedin.com/jobs/search/?f_E=2&keywords=business%20analyst%20coordinator'
};

export default function JobsPage() {
  const { user, getAuthHeader } = useAuth();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');

  // Filter jobs
  const filteredJobs = JOB_LISTINGS.filter(job => {
    const matchesCategory = activeCategory === 'all' || job.category === activeCategory;
    const matchesSearch = job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         job.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         job.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation = !location || job.location.toLowerCase().includes(location.toLowerCase());
    return matchesCategory && matchesSearch && matchesLocation;
  });

  const categoryColors = {
    tech: 'bg-blue-500/20 text-blue-400',
    trades: 'bg-amber-500/20 text-amber-400',
    healthcare: 'bg-emerald-500/20 text-emerald-400',
    business: 'bg-purple-500/20 text-purple-400',
    entry: 'bg-cyan-500/20 text-cyan-400'
  };

  return (
    <AppLayout>
      <PageHeader 
        title="Jobs & Opportunities"
        description="Find entry-level jobs, apprenticeships, and career opportunities"
      />

      {/* LinkedIn Integration Banner */}
      <div className="mb-6 p-4 bg-gradient-to-r from-[#0077b5]/20 to-blue-900/20 rounded-xl border border-[#0077b5]/30">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Linkedin className="w-8 h-8 text-[#0077b5]" />
            <div>
              <h3 className="text-white font-medium">Search LinkedIn Jobs</h3>
              <p className="text-slate-300 text-sm">Find thousands more opportunities on LinkedIn</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <a 
              href={LINKEDIN_SEARCH_URLS.entry}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-[#0077b5] hover:bg-[#006699] text-white rounded-lg text-sm flex items-center gap-2 transition-colors"
            >
              <GraduationCap className="w-4 h-4" />
              Entry Level
            </a>
            <a 
              href={LINKEDIN_SEARCH_URLS.trades}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-[#0077b5] hover:bg-[#006699] text-white rounded-lg text-sm flex items-center gap-2 transition-colors"
            >
              <Wrench className="w-4 h-4" />
              Trades
            </a>
            <a 
              href={LINKEDIN_SEARCH_URLS.tech}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-[#0077b5] hover:bg-[#006699] text-white rounded-lg text-sm flex items-center gap-2 transition-colors"
            >
              <Globe className="w-4 h-4" />
              Tech
            </a>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <GlassCard className="mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300 z-10" />
            <input
              type="text"
              placeholder="Search jobs, companies, keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-11 pr-4 py-2 text-white"
              data-testid="job-search"
            />
          </div>
          <div className="relative w-full md:w-48">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300 z-10" />
            <input
              type="text"
              placeholder="Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-11 pr-4 py-2 text-white"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mt-4">
          {JOB_CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all ${
                  activeCategory === cat.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-800 text-slate-300 hover:text-white'
                }`}
                data-testid={`cat-${cat.id}`}
              >
                <Icon className="w-4 h-4" />
                {cat.label}
              </button>
            );
          })}
        </div>
      </GlassCard>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Job Listings */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-medium">{filteredJobs.length} Jobs Found</h3>
            <span className="text-slate-300 text-sm">Entry-level & apprenticeships</span>
          </div>

          {filteredJobs.map((job) => (
            <motion.div
              key={job.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-4"
              data-testid={`job-${job.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="text-white font-medium text-lg">{job.title}</h4>
                  <div className="flex items-center gap-2 text-slate-300 text-sm mt-1">
                    <Building className="w-4 h-4" />
                    <span>{job.company}</span>
                    <span className="text-slate-300">•</span>
                    <MapPin className="w-4 h-4" />
                    <span>{job.location}</span>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs ${categoryColors[job.category] || categoryColors.entry}`}>
                  {job.category.charAt(0).toUpperCase() + job.category.slice(1)}
                </span>
              </div>

              <p className="text-slate-300 text-sm mb-3">{job.description}</p>

              <div className="flex flex-wrap gap-2 mb-3">
                <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  {job.salary}
                </span>
                <span className="px-3 py-1 bg-slate-700 text-slate-300 rounded-full text-sm">
                  {job.type}
                </span>
                <span className="px-3 py-1 bg-slate-700 text-slate-300 rounded-full text-sm flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {job.posted}
                </span>
              </div>

              {/* Requirements */}
              <div className="mb-3">
                <p className="text-slate-300 text-xs mb-1">Requirements:</p>
                <div className="flex flex-wrap gap-1">
                  {job.requirements.map((req, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-slate-800 text-slate-300 text-xs rounded">
                      {req}
                    </span>
                  ))}
                </div>
              </div>

              <a
                href={job.apply_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                data-testid={`apply-job-${job.id}`}
              >
                <ExternalLink className="w-4 h-4" />
                Apply on {job.apply_url.includes('linkedin') ? 'LinkedIn' : 'Indeed'}
              </a>
            </motion.div>
          ))}

          {filteredJobs.length === 0 && (
            <div className="text-center py-12 glass-card">
              <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-300">No jobs found matching your criteria</p>
              <p className="text-slate-300 text-sm mt-1">Try adjusting your search or filters</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Entrepreneurship Section */}
          <GlassCard>
            <div className="flex items-center gap-2 mb-4">
              <Rocket className="w-5 h-5 text-purple-400" />
              <h3 className="font-outfit text-lg font-semibold text-white">Start Your Own Business</h3>
            </div>
            <p className="text-slate-300 text-sm mb-4">
              Interested in entrepreneurship? Here are resources to help you start your own venture.
            </p>
            <div className="space-y-3">
              {ENTREPRENEURSHIP_RESOURCES.map((resource, idx) => {
                const Icon = resource.icon;
                return (
                  <a
                    key={idx}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors group"
                  >
                    <div className="flex items-start gap-3">
                      <Icon className="w-5 h-5 text-purple-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium group-hover:text-purple-400 transition-colors">
                          {resource.title}
                        </p>
                        <p className="text-slate-300 text-xs mt-0.5">{resource.description}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-purple-400 transition-colors" />
                    </div>
                  </a>
                );
              })}
            </div>
          </GlassCard>

          {/* Quick Stats */}
          <GlassCard>
            <h3 className="font-outfit text-lg font-semibold text-white mb-4">Job Market Insights</h3>
            <div className="space-y-3">
              <div className="p-3 bg-emerald-500/10 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-slate-300 text-sm">Skilled Trades Growth</span>
                  <span className="text-emerald-400 font-bold">+15%</span>
                </div>
                <p className="text-slate-300 text-xs mt-1">Expected growth by 2030</p>
              </div>
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-slate-300 text-sm">Tech Entry Jobs</span>
                  <span className="text-blue-400 font-bold">50K+</span>
                </div>
                <p className="text-slate-300 text-xs mt-1">Open positions nationwide</p>
              </div>
              <div className="p-3 bg-amber-500/10 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-slate-300 text-sm">Avg. Apprentice Wage</span>
                  <span className="text-amber-400 font-bold">$18-25/hr</span>
                </div>
                <p className="text-slate-300 text-xs mt-1">Earn while you learn</p>
              </div>
            </div>
          </GlassCard>

          {/* Indeed Search */}
          <GlassCard>
            <h3 className="font-outfit text-lg font-semibold text-white mb-4">More Job Boards</h3>
            <div className="space-y-2">
              <a
                href="https://www.indeed.com/jobs?q=entry+level"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors text-slate-300 hover:text-white"
              >
                <Globe className="w-4 h-4 text-blue-400" />
                <span className="text-sm flex-1">Indeed</span>
                <ExternalLink className="w-4 h-4" />
              </a>
              <a
                href="https://www.glassdoor.com/Job/entry-level-jobs-SRCH_KO0,11.htm"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors text-slate-300 hover:text-white"
              >
                <Globe className="w-4 h-4 text-emerald-400" />
                <span className="text-sm flex-1">Glassdoor</span>
                <ExternalLink className="w-4 h-4" />
              </a>
              <a
                href="https://www.ziprecruiter.com/Jobs/Entry-Level"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors text-slate-300 hover:text-white"
              >
                <Globe className="w-4 h-4 text-amber-400" />
                <span className="text-sm flex-1">ZipRecruiter</span>
                <ExternalLink className="w-4 h-4" />
              </a>
              <a
                href="https://www.apprenticeship.gov/apprenticeship-job-finder"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors text-slate-300 hover:text-white"
              >
                <Wrench className="w-4 h-4 text-purple-400" />
                <span className="text-sm flex-1">Apprenticeship.gov</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </GlassCard>
        </div>
      </div>
    </AppLayout>
  );
}
